import { CartAddEvent } from '@theme/events';

/**
 * Complementary product picker (variant metaobject → optional second ATC).
 *
 * - Syncs UI on Horizon `variant:update` via section HTML swap (same pattern as pairs-well-with).
 * - Selection UI reuses native variant-option markup + `dev-variant-drawer.js` overflow/drawer.
 * - On successful main product ATC (`product-form-component`), chains a second `/cart/add.js`
 *   when an option other than index 0 (“No”) is selected. Does not modify native ATC.
 */

const SOURCE = 'dev-complementary-product-picker';

class DevComplementaryProductPicker extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {boolean} */
  #chainInFlight = false;

  /** @type {string[]} */
  #pendingVariantIds = [];

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener('variant:update', this.#onVariantUpdate, { signal });

    this.addEventListener('change', this.#onChange, { signal });
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd, { signal });

    this.#syncFromChecked();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#pendingVariantIds = [];
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    const html = customEvent.detail?.data?.html;
    if (!html) return;

    if (customEvent.detail.data.newProduct) {
      this.dataset.productId = String(customEvent.detail.data.newProduct.id);
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const updated = html.querySelector(
      `dev-complementary-product-picker[data-block-id="${this.dataset.blockId}"]`
    );
    if (!updated) return;

    if (updated.hasAttribute('hidden')) {
      this.setAttribute('hidden', '');
    } else {
      this.removeAttribute('hidden');
    }

    this.innerHTML = updated.innerHTML;
    this.removeAttribute('data-complementary-variant-id');
    this.#syncFromChecked();
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('input[data-dev-cpp-option]')) return;
    this.#applySelection(target);
  };

  #syncFromChecked() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[data-dev-cpp-option]:checked')
    );
    if (checked) this.#applySelection(checked);
    else this.#clearAddonUi();
  }

  /** @param {HTMLInputElement} input */
  #applySelection(input) {
    const variantId = input.dataset.complementaryVariantId || '';
    const price = input.dataset.price || '';
    const url = input.dataset.url || '';

    const priceEl = this.querySelector('[data-dev-cpp-price]');
    const viewEl = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-dev-cpp-view]'));
    const legendValue = this.querySelector('[data-dev-cpp-legend-value]');
    const triggerValue = this.querySelector('[data-dev-cpp-trigger-value]');
    const labelHost = input.closest('label')?.querySelector('[data-dev-cpp-option-label]');
    const sourceLine = labelHost?.querySelector('[data-rolling-text-line]');
    const labelText = (sourceLine?.textContent ?? labelHost?.textContent)?.trim();

    if (labelText) {
      if (legendValue) legendValue.textContent = labelText;
      if (triggerValue) {
        const rollingLines = triggerValue.querySelectorAll('[data-rolling-text-line]');
        if (rollingLines.length > 0) {
          for (const line of rollingLines) {
            line.textContent = labelText;
          }
        } else {
          triggerValue.textContent = labelText;
        }
      }
    }

    if (variantId) {
      this.dataset.complementaryVariantId = variantId;
      if (priceEl instanceof HTMLElement) {
        priceEl.textContent = price;
        priceEl.hidden = !price;
      }
      if (viewEl) {
        if (url) {
          viewEl.href = url;
          viewEl.hidden = false;
        } else {
          viewEl.hidden = true;
        }
      }
    } else {
      this.#clearAddonUi();
    }
  }

  #clearAddonUi() {
    delete this.dataset.complementaryVariantId;
    const priceEl = this.querySelector('[data-dev-cpp-price]');
    const viewEl = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-dev-cpp-view]'));
    if (priceEl instanceof HTMLElement) {
      priceEl.textContent = '';
      priceEl.hidden = true;
    }
    if (viewEl) {
      viewEl.hidden = true;
      viewEl.removeAttribute('href');
    }
  }

  /** @param {Event} event */
  #onCartAdd = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    const data = customEvent.detail?.data;
    if (!data || data.didError) return;
    if (data.source !== 'product-form-component') return;
    if (String(data.productId) !== String(this.dataset.productId)) return;

    const complementaryId = this.dataset.complementaryVariantId;
    if (!complementaryId) return;

    this.#pendingVariantIds.push(complementaryId);
    this.#drainQueue();
  };

  #drainQueue() {
    if (this.#chainInFlight) return;

    const variantId = this.#pendingVariantIds.shift();
    if (!variantId) return;

    this.#chainInFlight = true;
    this.#chainComplementaryAdd(variantId).finally(() => {
      this.#chainInFlight = false;
      this.#drainQueue();
    });
  }

  /** @param {string} variantId */
  async #chainComplementaryAdd(variantId) {
    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    }

    const addUrl =
      (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) ||
      `${window.Shopify?.routes?.root || '/'}cart/add.js`;

    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const payload = await res.json();

      if (payload.status || payload.message) {
        console.warn('[dev-cpp] complementary add failed', payload.message || payload.description);
        return;
      }

      this.#emitAnalytics(payload, variantId);

      document.dispatchEvent(
        new CartAddEvent(payload, SOURCE, {
          source: SOURCE,
          itemCount: 1,
          productId: this.dataset.productId,
          variantId,
          sections: payload.sections,
        })
      );
    } catch (error) {
      console.warn('[dev-cpp] complementary add error', error);
    }
  }

  /**
   * @param {object} payload
   * @param {string} variantId
   */
  #emitAnalytics(payload, variantId) {
    const handler = /** @type {((detail: object) => void) | undefined} */ (
      window.complementaryProductHandleAddToCart
    );
    if (typeof handler === 'function') {
      handler({
        variantId,
        response: payload,
        productId: this.dataset.productId,
        add_to_cart_flag: 'Complementary product',
        item_complementary: '1',
      });
      return;
    }

    // Fallback when NoomaDL handler is missing. Prestige complementary sent both variant keys.
    // Unified target later: only item_variant_id.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'add_to_cart_nooma',
      add_to_cart_flag: 'Complementary product',
      item_complementary: '1',
      ecommerce: {
        items: [
          {
            item_variant_id: variantId,
            variant_id: variantId,
            item_complementary: '1',
            quantity: '1',
          },
        ],
      },
    });
  }
}

if (!customElements.get('dev-complementary-product-picker')) {
  customElements.define('dev-complementary-product-picker', DevComplementaryProductPicker);
}
